import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

interface TrainingCardDetail {
  id: number;
  section_id: number;
  title: string;
  description: string;
  embed_url: string;
  thumbnail_url: string;
  section_title: string;
}

const TrainingDetail: React.FC = () => {
  const { cardId } = useParams<{ cardId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [card, setCard] = useState<TrainingCardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const embedRef = useRef<HTMLDivElement>(null);

  // Inject embed HTML and execute script tags (React won't run scripts via dangerouslySetInnerHTML)
  const renderEmbed = useCallback((embedCode: string) => {
    if (!embedRef.current) return;
    // Clear previous content
    embedRef.current.innerHTML = '';

    // Parse the HTML and separate scripts from other content
    const temp = document.createElement('div');
    temp.innerHTML = embedCode;

    // Append non-script nodes first
    Array.from(temp.childNodes).forEach(node => {
      if (node.nodeName !== 'SCRIPT') {
        embedRef.current!.appendChild(node.cloneNode(true));
      }
    });

    // Then create and append script elements so they execute
    temp.querySelectorAll('script').forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }
      embedRef.current!.appendChild(newScript);
    });
  }, []);

  useEffect(() => {
    fetchCard();
  }, [cardId, token]);

  const fetchCard = async () => {
    try {
      const response = await fetch(`/api/training/cards/${cardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCard(data);
      } else {
        setError('Training card not found');
      }
    } catch (err) {
      setError('Failed to load training card');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h2 className="text-xl font-semibold text-gray-700 mb-2">{error || 'Not found'}</h2>
        <button
          onClick={() => navigate('/training')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Back to Training
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/training')}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Training
          </button>
          <div className="h-4 w-px bg-gray-300" />
          <span className="text-sm text-gray-400">{card.section_title}</span>
        </div>
      </div>

      {/* Video embed area */}
      <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
        <div className="max-w-5xl mx-auto">
          {card.embed_url ? (
            <EmbedRenderer embedCode={card.embed_url} renderEmbed={renderEmbed} embedRef={embedRef} />
          ) : (
            <div className="aspect-video flex items-center justify-center">
              <div className="text-center text-slate-400">
                <svg className="w-16 h-16 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <p>No video embed configured yet</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content below video */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{card.title}</h1>
        {card.description && (
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{card.description}</p>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-gray-200">
          <button
            onClick={() => navigate('/training')}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
          >
            Back to Training Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

// Separate component to handle embed rendering with proper script execution
const EmbedRenderer: React.FC<{
  embedCode: string;
  renderEmbed: (code: string) => void;
  embedRef: React.RefObject<HTMLDivElement | null>;
}> = ({ embedCode, renderEmbed, embedRef }) => {
  useEffect(() => {
    renderEmbed(embedCode);
    return () => {
      // Cleanup on unmount
      if (embedRef.current) {
        embedRef.current.innerHTML = '';
      }
    };
  }, [embedCode, renderEmbed, embedRef]);

  return <div ref={embedRef} className="storylane-embed" />;
};

export default TrainingDetail;
